import {
  CheckInput,
  Icon,
  SelectOptionType,
  TextAreaInput,
  TextInput,
} from '@octano/global-ui';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Row, UncontrolledTooltip } from 'reactstrap';

import SectionTitle from '../../../../../../components/text/SectionTitle';
import { useValidations } from '../../../../../../hooks/useValidations';

const prefix = 'tariffAndTuition.discounts';
const discountPrefix = `${prefix}.discount`;

interface FormValues {
  code: string;
  name: string;
  description: string;
  percentage: string;
  hasAutomaticAssignment: boolean;
  applyToAllNewAdmissionProcesses: boolean;
  minimumGradeAverage: string;
  inicioAcademicLevelIds: SelectOptionType[];
  inicioPeriodIds: SelectOptionType[];
  inicioStudyPlanIds: SelectOptionType[];
  inicioCampusIds: SelectOptionType[];
  inicioScheduleIds: SelectOptionType[];
  inicioModalityIds: SelectOptionType[];
  continuidadAcademicLevelIds: SelectOptionType[];
  continuidadStudyPlanIds: SelectOptionType[];
  continuidadCampusIds: SelectOptionType[];
  continuidadScheduleIds: SelectOptionType[];
  continuidadModalityIds: SelectOptionType[];
  continuidadPeriodIds: SelectOptionType[];
  enrollmentDates: { periodId: number; startDate: string; endDate: string }[];
}

interface DiscountDataSectionProps {
  editing: boolean;
}

const DiscountDataSection = ({ editing }: DiscountDataSectionProps) => {
  const { t } = useTranslation();
  const { control } = useFormContext<FormValues>();
  const { msgValidations, validateTextNotEmpty } = useValidations();

  return (
    <>
      <Row className="mb-3">
        <Col xs="12">
          <SectionTitle text={t(`${prefix}.discountData`)} />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col xs="12" md="6">
          <TextInput
            name="name"
            disabled={editing}
            label={t('common.forms.name')}
            control={control}
            rules={{
              required: msgValidations.required,
              maxLength: {
                value: 50,
                message: t('common.validations.maxLength', { length: 50 }),
              },
              minLength: {
                value: 3,
                message: t('common.validations.minLength', { length: 3 }),
              },
              validate: validateTextNotEmpty,
            }}
          />
        </Col>
        <Col xs="12" md="3">
          <TextInput
            name="code"
            control={control}
            disabled={editing}
            label={t('common.forms.code')}
            rules={{
              required: msgValidations.required,
              maxLength: {
                value: 15,
                message: t('common.validations.maxLength', { length: 15 }),
              },
              minLength: {
                value: 3,
                message: t('common.validations.minLength', { length: 3 }),
              },
              validate: validateTextNotEmpty,
            }}
          />
        </Col>
        <Col xs="12" md="3">
          <TextInput
            name="percentage"
            control={control}
            label={t(`${discountPrefix}.percentage`)}
            formatter={(value: string) => {
              const percentage = Number(value.replace(/\D/g, ''));
              return percentage < 1 || percentage > 100
                ? ''
                : String(percentage);
            }}
            rules={{
              required: msgValidations.required,
              validate: validateTextNotEmpty,
            }}
          />
        </Col>
      </Row>

      <Row className="mb-4">
        <Col xs="12">
          <TextAreaInput
            name="description"
            control={control}
            label={`${t('common.forms.description')}`}
            height={120}
            placeholder={t(`common.forms.descriptionPlaceholder`)}
            rules={{
              maxLength: {
                value: 200,
                message: t('common.validations.maxLength', { length: 200 }),
              },
            }}
          />
        </Col>
      </Row>

      <Row>
        <Col xs="12">
          <CheckInput
            name="hasAutomaticAssignment"
            label={t(`${prefix}.hasAutomaticAssignment`)}
            control={control}
          />
        </Col>

        <Col xs="12">
          <div className="d-flex align-items-start">
            <CheckInput
              name="applyToAllNewAdmissionProcesses"
              label={t(`${prefix}.applyToAllNewAdmissionProcesses`)}
              control={control}
            />
            <span
              id="applyToAllTooltip"
              className="d-flex align-items-start mt-1 ml-2"
              style={{ cursor: 'pointer' }}
            >
              <Icon name="information" size={16} color="primary" />
              <UncontrolledTooltip placement="right" target="applyToAllTooltip">
                {t(`${prefix}.applyToAllNewAdmissionProcessesTooltip`)}
              </UncontrolledTooltip>
            </span>
          </div>
        </Col>
      </Row>
    </>
  );
};

export default DiscountDataSection;
